import { apiFetch } from './client';
import {
  assertArray,
  assertMediaListItem,
  assertLoginCredentialDefinition,
  assertLoginConfirmResponse,
  assertTestModeNotificationResponse,
  assertStringArray,
} from './guards';

export enum MediaType {
  BEAUTIFUL_SOUPE = 1,
  EMAIL = 2,
  BEAUTIFUL_SOUPE_PERIODIC = 3,
}

export enum JobNumberCustomizePattern {
  NOT_SET = -1,
  CSV_CONTAINS_JOB_ID = 1, // represents media when CSV is available and contains job ID column, so job number can be directly obtained from CSV without customization
  CSV_DOES_NOT_CONTAIN_JOB_ID = 2, // represents media when CSV is available but does not contain job ID column, so job number needs to be customized with extract rules
  SCREEN_AVAILABLE_NO_CSV = 3, // represents media when jobID can be obtained from screen scraping but CSV is not available, so job number needs to be customized with extract rules
  NO_SCREEN_NO_CSV = 4, // represents media when neither screen scraping nor CSV is available, so job number needs to be customized with extract rules
  MAIL_CRAWLER = 5, // represents media when job number can be obtained from email crawling, so job number needs to be customized with extract rules
}

export enum LoginConfirmStatus {
  Success = 'success',
  Failure = 'failure',
}

export interface MediaListItem {
  id: number;
  mediaName: string;
  jobNumberCustomizePattern: JobNumberCustomizePattern;
  mediaCrawlerInfo: { baseUrl: string };
  mediaType: MediaType;
}

// Discriminated union type to enforce type-safety at compile time.
// EMAIL credentials may omit template; all other types must have a template.
export type LoginCredentialDefinition =
  | { type: MediaType.EMAIL; template?: string }
  | { type: Exclude<MediaType, MediaType.EMAIL>; template: string };

export interface LoginConfirmResponse {
  status: LoginConfirmStatus;
}

export interface TestModeNotificationResponse {
  notificationMessage: string[];
  testApplicationMailaddress: { mailAddress: string }[];
  testApplicationMailDomain: { mailDomain: string }[];
}

export async function getMediaList(): Promise<MediaListItem[]> {
  const raw = await apiFetch<unknown>('/api/v1/media');
  assertArray(raw);
  for (const item of raw) {
    assertMediaListItem(item);
  }
  return raw as MediaListItem[];
}

export async function getLoginCredentialDefinition(
  mediaId: number,
): Promise<LoginCredentialDefinition> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/login-credentials-definition`);
  assertLoginCredentialDefinition(raw);
  return raw;
}

export async function getCsvHeaders(mediaId: number): Promise<string[]> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/csv-headers`);
  assertStringArray(raw);
  return raw;
}

export async function postLoginConfirm(
  mediaId: number,
  body: { loginCredential: string },
): Promise<LoginConfirmResponse> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/login-confirm`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  assertLoginConfirmResponse(raw);
  return raw;
}

export async function getTestModeConfirmation(mediaId: number): Promise<string[]> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/test-mode-confirmation`);
  assertStringArray(raw);
  return raw;
}

export async function getTestModeNotification(
  mediaId: number,
): Promise<TestModeNotificationResponse> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/test-mode-notification`);
  assertTestModeNotificationResponse(raw);
  return raw;
}

export async function getGettingStartedCheckList(mediaId: number): Promise<string[]> {
  const raw = await apiFetch<unknown>(`/api/v1/media/${mediaId}/getting-started-check-list`);
  assertStringArray(raw);
  return raw;
}
